import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../config/firebase';
import { WebsiteMetadataForm } from './WebsiteMetadataForm';
import { BrandGenerationLoader } from '../brands/BrandGenerationLoader';
import type { SocialPlatform } from '../icons/SocialIcon';
import { mapAnalysisToBrand } from '../../utils/mapAnalysisToBrand';

// Helper function to normalize platform names to valid SocialPlatform values
const normalizePlatformName = (platform: string): string => {
  // Convert to lowercase and remove any spaces or special characters
  const normalized = platform.toLowerCase().trim();
  
  // Map of possible API responses to valid platform values
  const platformMap: Record<string, string> = {
    'linkedin': 'linkedin',
    'facebook': 'facebook',
    'instagram': 'instagram',
    'twitter': 'twitter',
    'youtube': 'youtube',
    'tiktok': 'tiktok',
    'pinterest': 'pinterest',
    'google business': 'google-business',
    'google-business': 'google-business',
    'googlebusiness': 'google-business'
  };
  
  return platformMap[normalized] || normalized;
};

export interface WebsiteFetchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WebsiteFetchModal({ isOpen, onClose }: WebsiteFetchModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleWebsiteSubmit = async (websiteUrl: string, shouldFetch: boolean) => {
    if (!/^https?:\/\//i.test(websiteUrl)) {
      websiteUrl = `https://${websiteUrl}`;
    }

    if (shouldFetch) {
      try {
        setIsAnalyzing(true);
        setError(null);

        // Get the current user's ID token
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch('https://api.socialness.ai/ai-coach/brand/analyze?WithVisual=true&fast=true', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Origin': 'https://socialness.ai'
          },
          body: JSON.stringify({
            url: websiteUrl,
            language: i18n.language.split('-')[0].toLowerCase()
          })
        });

        // Check if response is ok before trying to parse JSON
        if (!response.ok) {
          let errorMessage = `API returned status ${response.status}`;
          
          try {
            // Try to parse as JSON first
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (jsonError) {
            // If not JSON, try to get text content
            try {
              const errorText = await response.text();
              // If it's an HTML page (like a 503 error page), provide a more user-friendly message
              if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
                errorMessage = 'The analysis service is temporarily unavailable. Please try again in a few minutes.';
              } else {
                errorMessage = errorText || errorMessage;
              }
            } catch (textError) {
              // If we can't read the response at all, use the status code
              errorMessage = `Service temporarily unavailable (${response.status}). Please try again later.`;
            }
          }
          
          throw new Error(errorMessage);
        }

        const data = await response.json();

        // Add defensive checks to ensure analysis and its properties exist
        if (!data.analysis) {
          throw new Error(t('brands.websiteFetchModal.error.invalidResponse'));
        }

        const analysis = data.analysis || {};
        const brandIdentity = analysis.brandIdentity || {};
        const contentStrategy = analysis.contentStrategy || {};
        const overview = analysis.overview || {};

        // Log the data structure for debugging
        console.log('Analysis response:', {
          data,
          brandIdentity,
          contentStrategy,
          overview
        });

        // Use our mapping function to convert API response to BrandForm format
        const prefilledData = mapAnalysisToBrand(data, websiteUrl);

        navigate('/brand-strategy-lab/new', {
          state: {
            prefilledData
          }
        });
        onClose();

      } catch (error) {
        console.error('Error analyzing website:', error);
        setError(error instanceof Error ? error.message : t('brands.websiteFetchModal.error.fetch'));
      } finally {
        setIsAnalyzing(false);
      }
    } else {
      navigate('/brand-strategy-lab/new', {
        state: {
          prefilledData: {
            website: websiteUrl
          }
        }
      });
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-xl w-full rounded-lg bg-white p-6 border border-black shadow-lg">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">{t('brands.websiteFetchModal.ariaLabel.close')}</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-3 text-center sm:mt-0 sm:text-left">
            <WebsiteMetadataForm
              onSubmit={handleWebsiteSubmit}
              onSkip={() => {
                navigate('/brand-strategy-lab/new');
                onClose();
              }}
            />
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </Dialog.Panel>
      </div>

      {isAnalyzing && (
        <BrandGenerationLoader 
          isVisible={isAnalyzing}
        />
      )}
    </Dialog>
  );
}